import { SignInRedirect } from "./SignInRedirect.js";
import {
  ArrowLeft,
  ExternalLink,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import type { PlatformConversationReview as BoundedPlatformConversationReview } from "@opencloud/agent-protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { AgentApiError, api } from "../api.js";
import type { AgentBootstrap } from "../bootstrap.js";
import { agentBrand } from "../brand.js";
import type {
  AgentAppView,
  AgentRelease,
  AppDeploymentTruthView,
  ConversationSnapshot,
} from "../types.js";
import { Transcript } from "./Transcript.js";
import { DeploymentStatus } from "./DeploymentStatus.js";
import { ComposerTaskPlan } from "./ComposerTaskPlan.js";
import { latestConversationPlan } from "../task-presentation.js";

type PlatformConversationReviewPayload = BoundedPlatformConversationReview & {
  app: AgentAppView;
  deployment?: AppDeploymentTruthView | null;
  releases?: AgentRelease[];
};

export function PlatformConversationReview({
  bootstrap,
  appId,
}: {
  bootstrap: AgentBootstrap;
  appId: string;
}) {
  const [payload, setPayload] =
    useState<PlatformConversationReviewPayload | null>(null);
  const [error, setError] = useState<AgentApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await api<PlatformConversationReviewPayload>(
        `/v1/admin-review/apps/${encodeURIComponent(appId)}/conversation`,
      );
      setPayload(next);
      setError(null);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError
          : new Error("The conversation could not be opened."),
      );
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    void load();
    return () => {
      if (refreshTimer.current !== null) {
        window.clearTimeout(refreshTimer.current);
      }
    };
  }, [load]);

  useEffect(() => {
    if (!payload?.thread || !payload.snapshot) return;
    const target = `/v1/admin-review/apps/${encodeURIComponent(appId)}/threads/${encodeURIComponent(payload.thread.id)}/events?after=${payload.snapshot.cursor}`;
    const source = new EventSource(target);
    const schedule = () => {
      if (refreshTimer.current !== null) {
        window.clearTimeout(refreshTimer.current);
      }
      refreshTimer.current = window.setTimeout(() => void load(), 120);
    };
    source.addEventListener("agent.message.created", schedule);
    source.addEventListener("agent.run.status", schedule);
    source.addEventListener("agent.runtime.display", schedule);
    source.addEventListener("agent.runtime.ui-message", schedule);
    const poll = window.setInterval(schedule, 5_000);
    source.addEventListener("cursor", schedule);
    source.onopen = schedule;
    return () => { source.close(); window.clearInterval(poll); };
  }, [appId, load, payload?.snapshot?.cursor, payload?.thread?.id]);

  if (loading) {
    return (
      <ReviewState bootstrap={bootstrap}>
        <LoaderCircle className="a3-spin" aria-hidden="true" />
        <h1>Opening Agent conversation…</h1>
      </ReviewState>
    );
  }

  if (error) {
    if (error instanceof AgentApiError && error.status === 401) {
      return <SignInRedirect bootstrap={bootstrap} />;
    }
    const forbidden = error instanceof AgentApiError && error.status === 403;
    return (
      <ReviewState bootstrap={bootstrap}>
        <ShieldCheck aria-hidden="true" />
        <h1>
          {forbidden
            ? "Administrator access required"
            : "The conversation didn’t open"}
        </h1>
        <p>
          {forbidden
            ? "This view is available only to a signed-in platform administrator."
            : error.message}
        </p>
        <button
          type="button"
          className="a3-action a3-action-primary"
          onClick={() => {
            setLoading(true);
            void load();
          }}
        >
          <RefreshCw aria-hidden="true" /> Try again
        </button>
      </ReviewState>
    );
  }

  if (!payload?.thread || !payload.snapshot) {
    return (
      <ReviewState bootstrap={bootstrap}>
        <ShieldCheck aria-hidden="true" />
        <h1>No Agent conversation</h1>
        <p>This app no longer has an active Agent conversation to review.</p>
        <a className="a3-action a3-action-secondary" href={adminAppsUrl(bootstrap)}>
          <ArrowLeft aria-hidden="true" /> Back to admin apps
        </a>
      </ReviewState>
    );
  }

  const transcriptSnapshot = boundedReviewTranscriptSnapshot(
    payload,
    payload.thread,
    payload.snapshot,
  );
  const plan = latestConversationPlan(transcriptSnapshot);
  const brand = agentBrand();

  return (
    <div className="a3-theme a3-admin-review-shell">
      <header className="a3-admin-review-topbar">
        <a href={adminAppsUrl(bootstrap)} className="a3-action a3-action-secondary">
          <ArrowLeft aria-hidden="true" /> Admin apps
        </a>
        <div className="a3-admin-review-brand">
          <img src={bootstrap.logoUrl} alt="" aria-hidden="true" width="26" height="26" />
          <strong>{`${brand.productName} ${brand.agentName}`}</strong>
        </div>
      </header>
      <aside className="a3-admin-review-banner" role="note">
        <ShieldCheck aria-hidden="true" />
        <div>
          <strong>Administrator review · Read only</strong>
          <p>
            You are viewing the owner’s Agent conversation for safety review
            and diagnosis. This does not grant app membership or owner control,
            and you cannot send messages, answer questions, stop work, retry a
            run, or change Agent settings.
          </p>
        </div>
      </aside>
      <main className="a3-admin-review-workspace" aria-label="Agent conversation review">
        <header className="a3-admin-review-heading">
          <div>
            <span className="a3-admin-review-glyph" aria-hidden="true">
              {(payload.app.name ?? "A").slice(0, 2).toLocaleUpperCase()}
            </span>
            <div>
              <p>{payload.working ? "Agent working" : "Agent conversation"}</p>
              <h1>{payload.app.name ?? "New app"}</h1>
              <DeploymentStatus snapshot={transcriptSnapshot} />
            </div>
          </div>
          {payload.app.appUrl ? (
            <a
              className="a3-action a3-action-secondary"
              href={payload.app.appUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink aria-hidden="true" /> Open app
            </a>
          ) : null}
        </header>
        <Transcript
          snapshot={transcriptSnapshot}
          releases={payload.releases ?? []}
          appUrl={payload.app.appUrl}
          attachedOutcomeRunId={null}
          onRetry={() => undefined}
          onLoadRelease={async id => {
            const release = payload.releases?.find(item => item.id === id);
            if (!release) throw new Error("Release is outside the retained review window");
            return release;
          }}
          readOnly
        />
        <footer className="a3-admin-review-footer">
          {plan ? <ComposerTaskPlan plan={plan} readOnly /> : null}
          Conversation controls and provider details remain available only to
          the app owner.
        </footer>
      </main>
    </div>
  );
}

/**
 * The administrator wire contract intentionally omits owner-only provider,
 * harness and queue-membership fields. Adapt
 * only its bounded transcript data to the existing read-only renderer; these
 * neutral compatibility values never cross the HTTP boundary or enable an
 * owner control.
 */
export function boundedReviewTranscriptSnapshot(
  payload: PlatformConversationReviewPayload,
  thread: NonNullable<PlatformConversationReviewPayload["thread"]>,
  snapshot: NonNullable<PlatformConversationReviewPayload["snapshot"]>,
): ConversationSnapshot {
  const { app } = payload;
  return {
    schemaVersion: 1,
    cursor: snapshot.cursor,
    app: {
      id: app.id,
      name: app.name,
      slug: app.slug,
      appUrl: app.appUrl,
      state: app.state,
      activeDeploymentId: app.activeDeploymentId,
    },
    deployment: payload.deployment ?? null,
    thread: {
      id: thread.id,
      appId: thread.appId,
      fundingSource: "platform",
      providerConnectionId: null,
      platformAiModel: null,
      harnessVersion: "bounded-administrator-review",
      model: null,
      reasoningEffort: null,
      title: thread.title,
      status: thread.status,
      createdAt: thread.createdAt,
      updatedAt: thread.updatedAt,
    },
    messages: snapshot.messages.map(({ presentation, ...message }) =>
      presentation === undefined
        ? message
        : { ...message, presentation },
    ),
    runs: snapshot.runs.map((run) => ({
      ...run,
      blocker: null,
      queue: null,
    })),
    question: snapshot.question,
    activity: snapshot.activity,
    outcomes: snapshot.outcomes,
    cards: snapshot.cards.map(value => ({ ...value, card: { ...value.card,
      icon: value.card.icon ?? "check", styleColor: value.card.styleColor ?? "blue",
      sections: value.card.sections ?? [],
    } })),
    ...(snapshot.uiMessages === undefined ? {} : { uiMessages: snapshot.uiMessages }),
    taskLists: snapshot.taskLists,
    toolCalls: snapshot.toolCalls.map((toolCall) => ({
      ...toolCall,
      operationIds: toolCall.operationIds ?? [],
    })),
  };
}

function ReviewState({
  bootstrap,
  children,
}: {
  bootstrap: AgentBootstrap;
  children: React.ReactNode;
}) {
  return (
    <div className="a3-theme a3-review-state">
      <img src={bootstrap.logoUrl} alt="" aria-hidden="true" width="32" height="32" />
      {children}
    </div>
  );
}

function adminAppsUrl(bootstrap: AgentBootstrap): string {
  const url = new URL(bootstrap.appsUrl);
  url.pathname = "/admin";
  url.search = "?screen=apps";
  return url.toString();
}
